import React, { useState, useEffect } from "react";
import {
  User,
  Calendar,
  Building,
  MapPin,
  Phone,
  Mail,
  DollarSign,
  Award,
  Clock,
  Star,
  Edit,
  Camera,
  Shield,
  Briefcase,
  GraduationCap,
  Heart,
  Globe,
  Linkedin,
  Twitter,
  Github,
  ExternalLink,
  Download,
  Share2,
  Settings,
  Bell,
  Lock,
  Eye,
  EyeOff,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useLanguage } from "@/contexts/LanguageContext";
import { useUserInfo } from "@/hooks/useUserInfo";
import { getSummary } from "@/utils/pasUtils.js";
import { useNavigate } from "react-router-dom";
import DevelopmentAlert from "@/components/DevelopmentAlert.tsx";
import { Skeleton } from "@/components/ui/skeleton";

// The API sometimes returns a single object (or an empty value) instead of a list.
const asList = (value: unknown) =>
  Array.isArray(value) ? value : value && typeof value === "object" ? [value] : [];

// Mirrors the page layout so content doesn't jump when the profile arrives.
const MeSkeleton: React.FC = () => (
  <div className="w-full space-y-6" aria-busy="true">
    <div className="space-y-3">
      <Skeleton className="h-10 sm:h-12 w-32 rounded-xl" />
      <Skeleton className="h-5 w-56" />
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Profile + quick stats */}
      <div className="space-y-6">
        <div className="surface p-6 flex flex-col items-center gap-3">
          <Skeleton className="h-24 w-24 rounded-full" />
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-52" />
          <Skeleton className="h-4 w-36" />
        </div>
        <div className="surface p-6 space-y-4">
          <Skeleton className="h-5 w-28" />
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="flex items-center justify-between">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-5 w-16 rounded-full" />
            </div>
          ))}
        </div>
      </div>

      {/* Tabs + details */}
      <div className="lg:col-span-2 space-y-6">
        <Skeleton className="h-12 w-full rounded-full" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[1, 2].map((card) => (
            <div key={card} className="surface p-6 space-y-4">
              <Skeleton className="h-6 w-36" />
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex items-center justify-between">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-4 w-28" />
                </div>
              ))}
            </div>
          ))}
          <div className="surface p-6 md:col-span-2 space-y-4">
            <Skeleton className="h-6 w-36" />
            <Skeleton className="h-24 w-full rounded-2xl" />
          </div>
        </div>
      </div>
    </div>
  </div>
);
interface EmployeeProfile {
  id: string;
  employeeId: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  age: number;
  dateJoined: string;
  yearsOfService: number;
  position: string;
  department: string;
  manager: string;
  location: string;
  salary: {
    base: number;
    currency: string;
    benefits: number;
    total: number;
  };

  education: {
    degree: string;
    institution: string;
    year: string;
  }[];
  certifications: {
    name: string;
    issuer: string;
    date: string;
    expiry?: string;
  }[];
  emergencyContact: {
    name: string;
    relationship: string;
    phone: string;
  };
  preferences: {
    notifications: boolean;
    privacy: "public" | "private" | "team";
    theme: "light" | "dark" | "auto";
  };
  profilePicture?: string;
}

const Me: React.FC = () => {
  const VITE_USERNAMET = import.meta.env.VITE_USERNAMET
  const VITE_PASSWORD = import.meta.env.VITE_PASSWORD

  const { t } = useLanguage();
  const navigate = useNavigate();
  const {
    fullName,
    email,
    role,
    profilePictureUrl,
    jobTitle,
    department,
    officeLocation,
  } = useUserInfo();

  const [editing, setEditing] = useState(false);
  const [showSalary, setShowSalary] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  const [data, setData] = useState({
    profile: null,
    educations: [],
    qualifications: [],
    teams: [],
    salary_structure: null,
    salaryBasic: 0,
    salaryHousing: 0,
    salaryTransportation: 0,
    salaryAchievingGoals: 0,
    loading: true,
    error: null

  });

  useEffect(() => {
    // The signed-in user's email arrives asynchronously; calling the API
    // without it returns a server error.
    if (!email) return;

    let cancelled = false;

    async function fetchProfile() {
      setData((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const summaryResponse = await getSummary(
          VITE_USERNAMET,
          VITE_PASSWORD,
          email
        );
        if (cancelled) return;
        const earnings = asList(summaryResponse?.salary_structure?.earnings);
        setData((prev) => ({
          ...prev,
          profile: summaryResponse?.profile ?? null,
          educations: asList(summaryResponse?.educations),
          teams: asList(summaryResponse?.teams),
          qualifications: asList(summaryResponse?.qualifications),
          salary_structure: summaryResponse?.salary_structure ?? null,
          salaryBasic: earnings[0]?.amount || 0,
          salaryHousing: earnings[1]?.amount || 0,
          salaryTransportation: earnings[2]?.amount || 0,
          salaryAchievingGoals: earnings[3]?.amount || 0,
          loading: false,
          error: null,
        }));
      } catch (err) {
        if (cancelled) return;
        setData((prev) => ({
          ...prev,
          loading: false,
          error: err?.message ?? String(err),
        }));
      }
    }

    fetchProfile();
    return () => {
      cancelled = true;
    };
  }, [email, VITE_USERNAMET, VITE_PASSWORD]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("ar-SA", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat("en-us", {
      style: "currency",
      currency: "ريـال سعودي" === currency ? "ريـال سعودي" : currency,
    }).format(amount);
  };


  const calculateAge = (dob) => {
    const birthDate = new Date(dob);

    const today = new Date();

    let age = today.getFullYear() - birthDate.getFullYear();

    const monthDifference = today.getMonth() - birthDate.getMonth();

    if (
      monthDifference < 0 ||
      (monthDifference === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    return age;
  };

  if (data.loading) {
    return <MeSkeleton />;
  }

  if (!data.profile) {
    return (
      <div className="surface text-center py-16 px-6">
        <User className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-semibold mb-2">Profile not found</h3>
        <p className="text-muted-foreground">
          Unable to load your profile information
        </p>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      <DevelopmentAlert />
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight">{t("me.title")}</h1>
          <p className="text-muted-foreground mt-2 sm:text-lg">{t("me.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          {/*<Button variant="outline" size="sm" onClick={() => navigate('/me/print')}>*/}
          {/*  <Download className="h-4 w-4 mr-2" />*/}
          {/*  {t("me.export_profile")}*/}
          {/*</Button>*/}
          {/*<Button variant="outline" size="sm">*/}
          {/*  <Share2 className="h-4 w-4 mr-2" />*/}
          {/*  {t("me.share")}*/}
          {/*</Button>*/}
          {/* <Button
              variant={editing ? "destructive" : "default"}
              size="sm"
              onClick={() => setEditing(!editing)}
            >
              <Edit className="h-4 w-4 mr-2" />
              {editing ? t("me.cancel_edit") : t("me.edit")}
            </Button> */}
        </div>
      </div>

      {/* Main Profile Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Profile Card */}
        <div className="lg:col-span-1 space-y-6">
          {/* Profile Card */}
          <Card className="text-center">
            <CardContent className="p-4 sm:p-6">
              <div className="relative mb-4">
                <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                  {profilePictureUrl ? (
                    <img
                      src={profilePictureUrl}
                      alt={fullName}
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    <User className="h-12 w-12 text-primary" />
                  )}
                </div>
                {editing && (
                  <Button
                    size="sm"
                    className="absolute bottom-0 right-0 rounded-full w-8 h-8 p-0"
                  >
                    <Camera className="h-4 w-4" />
                  </Button>
                )}
              </div>

              <h2 className="text-xl font-bold mb-2">
                {data.profile.full_name_arabic}
              </h2>
              <p className="text-muted-foreground mb-3">
                {data.profile.designation_arabic}
              </p>

              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-center gap-2">
                  <Building className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="break-words min-w-0">{data.profile.department}</span>
                </div>
                {/* <div className="flex items-center justify-center gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span>{profile.location}</span>
                </div> */}
                <div className="flex items-center justify-center gap-2">
                  <Mail className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="break-all min-w-0">{email}</span>
                </div>
              </div>


            </CardContent>
          </Card>

          {/* Quick Stats */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{t("me.quick_stats")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {t("me.years_of_service")}
                </span>
                <Badge variant="secondary" className="text-end whitespace-normal">
                  {calculateAge(data.profile.date_of_joining)} {t("me.years")}
                </Badge>
              </div>
              <div className="flex justify-between items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {t("me.age")}
                </span>
                <Badge variant="secondary" className="text-end whitespace-normal">
                  {calculateAge(data.profile.date_of_birth)} {t("me.year")}
                </Badge>
              </div>
              <div className="flex justify-between items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {t("me.join_date")}
                </span>
                <Badge variant="secondary" className="text-end whitespace-normal">
                  {data.profile.date_of_joining}
                </Badge>
              </div>
              <div className="flex justify-between items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {t("me.direct_manager")}
                </span>
                <Badge variant="outline" className="text-end whitespace-normal">
                  {data.profile.reports_to
                    ? data.profile.reports_to
                    : "غير محدد"}
                </Badge>
              </div>
            </CardContent>
          </Card>
          {/* Teams */}
          {data?.teams && data?.teams?.length > 0 && <Card>
            <CardHeader>
              <CardTitle className="text-lg">{t("me.teams")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-4">
                {data?.teams?.map((emp, index) => (
                  <div
                    key={index}
                    className="p-1 "
                  >
                    <h4 className="font-semibold mb-1 break-words">{emp.employee}- {emp.employee_name}</h4>
                  </div>
                ))}
              </div>

            </CardContent>
          </Card>}
        </div>

        {/* Right Column - Detailed Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Tabs */}
          <div role="tablist" className="grid grid-cols-3 gap-1 bg-card border border-border/60 p-1 rounded-full">
            {[
              { id: "overview", label: t("me.overview"), icon: User },
              {
                id: "professional",
                label: t("me.education_certifications"),
                icon: Briefcase,
              },

              { id: "personal", label: t("me.personal"), icon: Heart },
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <Button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  variant="ghost"
                  size="sm"
                  onClick={() => setActiveTab(tab.id)}
                  className={`${activeTab === tab.id ? "bg-ink text-white hover:bg-ink hover:text-white" : "text-muted-foreground"} h-auto min-h-10 flex-col sm:flex-row gap-1 sm:gap-2 px-1 sm:px-3 py-2 whitespace-normal text-xs sm:text-sm leading-tight`}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  {tab.label}
                </Button>
              );
            })}
          </div>

          {/* Tab Content */}
          <div className="space-y-6">
            {/* Overview Tab */}
            {activeTab === "overview" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <User className="h-5 w-5" />
                      {t("me.basic_info")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between gap-4">
                      <span className="text-sm text-muted-foreground">
                        رقم الموظف
                      </span>
                      <span className="font-medium text-end break-words min-w-0">{data.profile.name}</span>
                    </div>
                    <div className="flex justify-between gap-4">
                      <span className="text-sm text-muted-foreground">
                        الاسم الأول
                      </span>
                      <span className="font-medium text-end break-words min-w-0">
                        {data.profile.custom_first_name_ar}
                      </span>
                    </div>
                    <div className="flex justify-between gap-4">
                      <span className="text-sm text-muted-foreground">
                        اسم العائلة
                      </span>
                      <span className="font-medium text-end break-words min-w-0">
                        {data.profile.custom_third_name_ar}
                      </span>
                    </div>
                    <div className="flex justify-between gap-4">
                      <span className="text-sm text-muted-foreground">
                        تاريخ الميلاد
                      </span>
                      <span className="font-medium text-end break-words min-w-0">
                        {data.profile.date_of_birth}
                      </span>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Building className="h-5 w-5" />
                      {t("me.work_info")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between gap-4">
                      <span className="text-sm text-muted-foreground">
                        المنصب
                      </span>
                      <span className="font-medium text-end break-words min-w-0">
                        {data.profile.designation_arabic}
                      </span>
                    </div>
                    <div className="flex justify-between gap-4">
                      <span className="text-sm text-muted-foreground">
                        القسم
                      </span>
                      <span className="font-medium text-end break-words min-w-0">
                        {data.profile.department}
                      </span>
                    </div>
                    <div className="flex justify-between gap-4">
                      <span className="text-sm text-muted-foreground">
                        المدير
                      </span>
                      <span className="font-medium text-end break-words min-w-0">
                        {data.profile.reports_to
                          ? data.profile.reports_to
                          : "غير محدد"}
                      </span>
                    </div>
                    {/* <div className="flex justify-between gap-4">
                      <span className="text-sm text-muted-foreground">
                        الموقع
                      </span>
                      <span className="font-medium text-end break-words min-w-0">{profile.location}</span>
                    </div> */}
                  </CardContent>
                </Card>

                {/* Salary Information */}
                <Card className="md:col-span-2">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <DollarSign className="h-5 w-5" />
                      {t("me.salary_info")}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowSalary(!showSalary)}
                        className="ms-auto"
                      >
                        {showSalary ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {showSalary ? (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="text-center p-4 bg-muted/30 rounded-lg">
                          <p className="text-sm text-muted-foreground mb-1">
                            {t("me.base_salary")}
                          </p>
                          <p className="text-lg sm:text-xl font-bold text-primary">
                            {/* {formatCurrency(
                                data.salaryBasic,
                                profile.salary.currency
                              )} */}
                            {data.salaryBasic} ريال سعودي
                          </p>
                        </div>
                        <div className="text-center p-4 bg-muted/30 rounded-lg">
                          <p className="text-sm text-muted-foreground mb-1">
                            {t("me.benefits")}
                          </p>
                          <p className="text-lg sm:text-xl font-bold text-success">
                            {/* {formatCurrency(
                                data.salaryHousing + data.salaryTransportation + data.salaryAchievingGoals,
                                profile.salary.currency
                              )} */}
                            {data.salaryHousing +
                              data.salaryTransportation +
                              data.salaryAchievingGoals}{" "}
                            ريال سعودي
                          </p>
                        </div>
                        <div className="text-center p-4 bg-muted/30 rounded-lg">
                          <p className="text-sm text-muted-foreground mb-1">
                            {t("me.total_salary")}
                          </p>
                          <p className="text-lg sm:text-xl font-bold text-foreground">
                            {/* {formatCurrency(
                                data.salaryBasic + data.salaryHousing + data.salaryTransportation + data.salaryAchievingGoals,
                                profile.salary.currency
                              )} */}
                            {data.salaryBasic +
                              data.salaryHousing +
                              data.salaryTransportation +
                              data.salaryAchievingGoals}{" "}
                            ريال سعودي
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Lock className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                        <p className="text-muted-foreground">
                          {t("me.click_eye_to_view")}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Professional Tab */}
            {activeTab === "professional" && (
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <GraduationCap className="h-5 w-5" />
                      التعليم
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {data.educations?.map((edu, index) => (
                        <div
                          key={index}
                          className="p-4 border border-border rounded-lg"
                        >
                          <h4 className="font-semibold mb-1">{edu.class_per}</h4>
                          <p className="text-muted-foreground mb-1">
                            {edu.school_univ}
                          </p>
                          <Badge variant="outline">{edu.year_of_passing}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Award className="h-5 w-5" />
                      الشهادات
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {data.qualifications?.map((cert, index) => (
                        <div
                          key={index}
                          className="p-4 border border-border rounded-lg"
                        >
                          <h4 className="font-semibold mb-1">{cert.certificate_name}</h4>
                          <p className="text-muted-foreground mb-1">
                            {cert.institution_name}
                          </p>
                          <div className="flex flex-wrap gap-2">
                            <Badge variant="outline">{cert.from_date}</Badge>
                            {cert.to_date && (
                              <Badge variant="secondary">
                                ينتهي: {cert.to_date}
                              </Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}


            {/* Personal Tab */}
            {activeTab === "personal" && (
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Heart className="h-5 w-5" />
                      معلومات شخصية
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm text-muted-foreground">
                          رقم الهاتف
                        </label>
                        <p className="font-medium">
                          {data.profile.cell_number}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground">
                          تاريخ الميلاد
                        </label>
                        <p className="font-medium">
                          {data.profile.date_of_birth}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Shield className="h-5 w-5" />
                      جهة اتصال الطوارئ
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm text-muted-foreground">
                          الاسم
                        </label>
                        <p className="font-medium">
                          {/* {profile.emergencyContact.name} */}
                          {data.profile.person_to_be_contacted
                            ? data.profile.person_to_be_contacted
                            : "غير محدد"}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground">
                          العلاقة
                        </label>
                        <p className="font-medium">
                          {/* {profile.emergencyContact.relationship} */}
                          {data.profile.relation
                            ? data.profile.relation
                            : "غير محدد"}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground">
                          رقم الهاتف
                        </label>
                        <p className="font-medium">
                          {/* {profile.emergencyContact.phone} */}
                          {data.profile.emergency_contact_number
                            ? data.profile.emergency_contact_number
                            : "غير محدد"}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Settings className="h-5 w-5" />
                        التفضيلات
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center gap-2">
                          <span>الإشعارات</span>
                          <Badge
                            variant={
                              profile.preferences.notifications
                                ? "default"
                                : "secondary"
                            }
                          >
                            {profile.preferences.notifications
                              ? "مفعلة"
                              : "معطلة"}
                          </Badge>
                        </div>
                        <div className="flex justify-between items-center gap-2">
                          <span>خصوصية الملف الشخصي</span>
                          <Badge variant="outline">
                            {profile.preferences.privacy}
                          </Badge>
                        </div>
                        <div className="flex justify-between items-center gap-2">
                          <span>المظهر</span>
                          <Badge variant="outline">
                            {profile.preferences.theme}
                          </Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card> */}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Me;
